class SecureStore {
    private static instance: SecureStore;
    private encryptionKey: string | null = null;
    private symmetricKey: string | null = null;
    private vaultKey: string | null = null;
    private keysLoaded: boolean = false;
    // Cache for decrypted vault-level keys (v2 shared vaults)
    private vaultKeys: Map<string, string> = new Map(); // Changed to string to support UUID vault IDs
    
    // Check if we're in extension context
    private isExtension(): boolean {
        return typeof chrome !== 'undefined' && !!chrome.runtime && !!chrome.runtime.id;
    }

    private constructor() {
        // Initialize keys from sync storage (web context only)
        if (!this.isExtension()) {
            this.loadKeysFromLocalStorage();
        }
    }

    private loadKeysFromLocalStorage() {
        // Web context - load from localStorage
        this.encryptionKey = localStorage.getItem('encryptionKey');
        this.symmetricKey = localStorage.getItem('symmetricKey');
        this.vaultKey = localStorage.getItem('vaultKey');
        this.keysLoaded = true;
        console.log('SecureStore: Keys loaded from localStorage:', {
            hasEncryptionKey: !!this.encryptionKey,
            hasSymmetricKey: !!this.symmetricKey,
            hasVaultKey: !!this.vaultKey
        });
    }

    // New method to load keys from chrome storage asynchronously
    private async loadKeysFromChromeStorage(): Promise<void> {
        return new Promise((resolve) => {
            if (!this.isExtension()) {
                resolve();
                return;
            }

            chrome.storage.local.get(['auth', 'encryptionKey', 'symmetricKey', 'vaultKey'], (result) => {
                console.log('SecureStore: Loading keys from chrome.storage:', result);
                
                // Try auth object first (new format)
                if (result.auth) {
                    this.encryptionKey = result.auth.encryptionKey || null;
                    this.vaultKey = result.auth.vaultKey || null;
                    this.symmetricKey = result.auth.symmetricKey || null;
                }
                
                // Fallback to direct storage (old format)
                if (!this.encryptionKey && result.encryptionKey) {
                    this.encryptionKey = result.encryptionKey;
                }
                if (!this.vaultKey && result.vaultKey) {
                    this.vaultKey = result.vaultKey;
                }
                if (!this.symmetricKey && result.symmetricKey) {
                    this.symmetricKey = result.symmetricKey;
                }

                this.keysLoaded = true;
                console.log('SecureStore: Keys loaded from chrome.storage:', {
                    hasEncryptionKey: !!this.encryptionKey,
                    hasSymmetricKey: !!this.symmetricKey,
                    hasVaultKey: !!this.vaultKey
                });
                
                resolve();
            });
        });
    }

    static getInstance(): SecureStore {
        if (!SecureStore.instance) {
            SecureStore.instance = new SecureStore();
        }
        return SecureStore.instance;
    }

    // New async method to ensure keys are loaded before use
    async ensureKeysLoaded(): Promise<void> {
        if (this.keysLoaded) {
            return;
        }

        if (this.isExtension()) {
            await this.loadKeysFromChromeStorage();
        } else {
            this.loadKeysFromLocalStorage();
        }
    }

    // Modified to handle extension context with async loading
    getVaultKey(): string {
        if (this.isExtension()) {
            // For extension, use vaultKey (symmetric key used for encryption)
            const key = this.vaultKey || this.symmetricKey;
            if (!key) {
                console.error('SecureStore: Vault key not found. Keys loaded:', this.keysLoaded);
                throw new Error('Vault key not found. Please login again.');
            }
            return key;
        } else {
            // Web context remains the same
            if (!this.vaultKey) {
                throw new Error('Vault key not found. Please login again.');
            }
            return this.vaultKey;
        }
    }

    // Modified to handle extension context
    setVaultKey(vaultKey: string) {
        if (!vaultKey) {
            throw new Error('Cannot set empty vault key');
        }
        this.vaultKey = vaultKey;
        
        if (this.isExtension()) {
            // In extension, update the auth object
            chrome.storage.local.get(['auth'], (result) => {
                const auth = result.auth || {};
                chrome.storage.local.set({
                    auth: {
                        ...auth,
                        vaultKey: vaultKey
                    }
                });
            });
        } else {
            localStorage.setItem('vaultKey', vaultKey);
        }
    }

    // Modified to handle extension context
    setEncryptionKey(encryptionKey: string) {
        if (!encryptionKey) {
            throw new Error('Cannot set empty encryption key');
        }
        this.encryptionKey = encryptionKey;
        
        if (this.isExtension()) {
            // In extension, update the auth object
            chrome.storage.local.get(['auth'], (result) => {
                const auth = result.auth || {};
                chrome.storage.local.set({
                    auth: {
                        ...auth,
                        encryptionKey: encryptionKey
                    }
                });
            });
        } else {
            localStorage.setItem('encryptionKey', encryptionKey);
        }
    }

    // Modified to handle extension context
    setSymmetricKey(symmetricKey: string) {
        if (!symmetricKey) {
            throw new Error('Cannot set empty symmetric key');
        }
        this.symmetricKey = symmetricKey;
        
        if (this.isExtension()) {
            // In extension, update the auth object
            chrome.storage.local.get(['auth'], (result) => {
                const auth = result.auth || {};
                chrome.storage.local.set({
                    auth: {
                        ...auth,
                        symmetricKey: symmetricKey
                    }
                });
            });
        } else {
            localStorage.setItem('symmetricKey', symmetricKey);
        }
    }

    // Modified to handle extension context
    clearKeys(): void {
        this.encryptionKey = null;
        this.symmetricKey = null;
        this.vaultKey = null;
        this.vaultKeys.clear(); // Clear cached vault-level keys

        if (this.isExtension()) {
            // In extension, clear keys from auth object
            chrome.storage.local.get(['auth'], (result) => {
                const auth = result.auth || {};
                delete auth.encryptionKey;
                delete auth.symmetricKey;
                delete auth.vaultKey;
                chrome.storage.local.set({ auth });
            });
        } else {
            localStorage.removeItem('encryptionKey');
            localStorage.removeItem('symmetricKey');
            localStorage.removeItem('vaultKey');
        }
    }

    // Modified to handle extension context with immediate update
    setKeys(encryptionKey: string, symmetricKey: string, vaultKey: string) {
        if (!encryptionKey || !symmetricKey || !vaultKey) {
            throw new Error('Cannot set empty keys');
        }

        console.log('SecureStore: Setting keys', {
            hasEncryptionKey: !!encryptionKey,
            hasSymmetricKey: !!symmetricKey,
            hasVaultKey: !!vaultKey,
            isExtension: this.isExtension()
        });

        this.encryptionKey = encryptionKey;
        this.symmetricKey = symmetricKey;
        this.vaultKey = vaultKey;
        this.keysLoaded = true;

        if (this.isExtension()) {
            // In extension, update all keys in auth object AND store separately for redundancy
            chrome.storage.local.get(['auth'], (result) => {
                const auth = result.auth || {};
                chrome.storage.local.set({
                    auth: {
                        ...auth,
                        encryptionKey,
                        symmetricKey,
                        vaultKey
                    },
                    // Also store separately for easier access
                    encryptionKey,
                    symmetricKey,
                    vaultKey
                }, () => {
                    console.log('SecureStore: Keys saved to chrome.storage');
                });
            });
        } else {
            localStorage.setItem('encryptionKey', encryptionKey);
            localStorage.setItem('symmetricKey', symmetricKey);
            localStorage.setItem('vaultKey', vaultKey);
            console.log('SecureStore: Keys saved to localStorage');
        }
    }

    // Add the missing method for initializing from vault key
    initializeFromVaultKey(token: string, encryptedVaultKey: string, masterPassword: string): void {
        if (!token || !encryptedVaultKey || !masterPassword) {
            throw new Error('Missing required parameters for key initialization');
        }

        try {
            // Step 1: Derive encryption key from master password
            // This is a simplified example - you might use a more sophisticated approach
            const derivedEncryptionKey = this.deriveEncryptionKey(masterPassword);
            this.setEncryptionKey(derivedEncryptionKey);

            // Step 2: Decrypt vault key using encryption key
            const decryptedVaultKey = this.decryptVaultKey(encryptedVaultKey, derivedEncryptionKey);
            this.setVaultKey(decryptedVaultKey);

            // Step 3: Generate symmetric key using vault key and token
            const derivedSymmetricKey = this.deriveSymmetricKey(decryptedVaultKey, token);
            this.setSymmetricKey(derivedSymmetricKey);

            console.log('Encryption keys initialized successfully');
        } catch (error) {
            console.error('Failed to initialize encryption keys:', error);
            throw new Error('Failed to initialize encryption keys: ' + (error as Error).message);
        }
    }

    // Helper methods for key derivation (implement according to your encryption scheme)
    private deriveEncryptionKey(masterPassword: string): string {
        // In a real implementation, you would use a proper key derivation function like PBKDF2
        // This is a placeholder - replace with your actual implementation
        const CryptoJS = require('crypto-js');
        const salt = 'your-salt-value'; // Should be stored securely
        return CryptoJS.PBKDF2(masterPassword, salt, { keySize: 256/32, iterations: 10000 }).toString();
    }

    private decryptVaultKey(encryptedVaultKey: string, encryptionKey: string): string {
        // Placeholder for actual decryption logic
        const CryptoJS = require('crypto-js');
        try {
            const decrypted = CryptoJS.AES.decrypt(encryptedVaultKey, encryptionKey).toString(CryptoJS.enc.Utf8);
            if (!decrypted) {
                throw new Error('Decryption resulted in empty string');
            }
            return decrypted;
        } catch (error) {
            console.error('Failed to decrypt vault key:', error);
            throw new Error('Failed to decrypt vault key');
        }
    }

    private deriveSymmetricKey(vaultKey: string, token: string): string {
        // Placeholder for symmetric key derivation
        const CryptoJS = require('crypto-js');
        return CryptoJS.HmacSHA256(token, vaultKey).toString();
    }

    getEncryptionKey(): string {
        if (!this.encryptionKey) throw new Error('Encryption key not set');
        return this.encryptionKey;
    }

    getSymmetricKey(): string {
        if (!this.symmetricKey) throw new Error('Symmetric key not set');
        return this.symmetricKey;
    }

    // Vault-level key cache methods for v2 shared vaults
    setVaultLevelKey(vaultId: number | string, decryptedKey: string): void {
        if (!vaultId || !decryptedKey) {
            throw new Error('Invalid vault ID or key');
        }
        const keyId = String(vaultId); // Convert to string to support UUID
        this.vaultKeys.set(keyId, decryptedKey);
        console.log(`SecureStore: Cached vault-level key for vault ${vaultId}`);
    }

    getVaultLevelKey(vaultId: number | string): string | null {
        const keyId = String(vaultId); // Convert to string to support UUID
        const key = this.vaultKeys.get(keyId);
        if (!key) {
            console.warn(`SecureStore: No cached vault-level key for vault ${vaultId}`);
            return null;
        }
        return key;
    }

    hasVaultLevelKey(vaultId: number | string): boolean {
        const keyId = String(vaultId); // Convert to string to support UUID
        return this.vaultKeys.has(keyId);
    }

    clearVaultKeys(): void {
        this.vaultKeys.clear();
        console.log('SecureStore: Cleared all vault-level keys');
    }
}

export const secureStore = SecureStore.getInstance();